import { useState, useEffect } from 'react';
import { buildProductionOperationFilter, machineStatuses } from '@components/wallboard/components/functions';
import { WALLBOARD_TOUR_IDS } from '@components/tours/config/wallboard/constants';
import { isWeb } from '@helpers';
import type { Machine, WallboardBatch } from '@components/wallboard/types';
import type { OperationSearchItem } from '@interfaces/search';

export interface UseManageItemParams {
  machine: Machine;
  index: number;
  generateImageURL: (...args: any[]) => Promise<string>;
  blobAccessInfo: Record<string, any>;
  config: { tileHeight: number; tileWidth: number; scaleFactor: number };
  machineDetailsConditions: (args: { status: string; ncFileStatus: string | null; subStatus: string | null; ncFileExist: boolean }) => { machineActive: boolean };
  wallboardSelectedLevel: string;
  productionBatchStatistics: (args: { productionBatchId: number }) => Promise<any>;
  operationsSearch: (args: Record<string, any>) => Promise<any>;
  useForceUpdateEverySecond: () => void;
  oeeData: Record<string, { oee?: number }>;
}

export interface UseManageItemReturn {
  url: string | null;
  batch: WallboardBatch | null;
  jobs: OperationSearchItem[];
  firstScheduledJob: OperationSearchItem | null;
  oeeValue: number | null;
  machineActive: boolean;
  isActiveMachineStatus: boolean;
  itemBackgroundColor: string;
  tourIds: Record<string, string> | undefined;
  tileWebProps: Record<string, any>;
  headerWebProps: Record<string, any>;
  machineId: number;
  shortName: string;
  machineStatus: NonNullable<Machine['machineStatus']>;
  oldMachineStatusDate: number | null;
  oldMachineStatus: Machine['machineStatus'] | null;
  productionBatchId: number | null;
}

const useManageItem = ({
  machine,
  index,
  generateImageURL,
  blobAccessInfo,
  config,
  machineDetailsConditions,
  wallboardSelectedLevel,
  productionBatchStatistics,
  operationsSearch,
  useForceUpdateEverySecond,
  oeeData,
}: UseManageItemParams): UseManageItemReturn => {
  useForceUpdateEverySecond();

  const { oldMachineStatusDate, oldMachineStatus, machineStatus, machineId, shortName } = machine;
  const { imageData, imageHash, status, subStatus, ncFileStatus, partDetails, productionBatchId, ncFile } = machineStatus;

  const [url, setImageLink] = useState<string | null>(null);
  const [batch, setBatch] = useState<WallboardBatch | null>(machine.demoBatch || null);
  const [jobs, setJobs] = useState<OperationSearchItem[]>([]);

  useEffect(() => {
    (async () => {
      if (imageHash) {
        const imageLink = await generateImageURL({
          size: '_3x',
          cropped: imageData?.isCropped,
          item: { imageHash, extensionDerivative: imageData?.extensionDerivative },
          page: imageData?.pageNumber,
          ...blobAccessInfo,
        });
        setImageLink(imageLink);
      }
    })();
  }, [imageHash]);

  useEffect(() => {
    if (machine.demoBatch) {
      setBatch(machine.demoBatch);
      return;
    }

    if (!productionBatchId || !productionBatchStatistics) {
      return;
    }

    const fetchBatch = async () => {
      const res = await productionBatchStatistics({ productionBatchId });
      if (res?.statistics) {
        setBatch(res.statistics);
      }
    };

    fetchBatch();
  }, [productionBatchId, ncFileStatus, machine.demoBatch]);

  const { machineActive } = machineDetailsConditions({
    status,
    ncFileStatus,
    subStatus,
    ncFileExist: !!ncFile,
  });

  useEffect(() => {
    const links = batch?.links ?? [];
    if (!links.length) {
      setJobs([]);
      return;
    }

    const filterQuery = buildProductionOperationFilter(links);
    if (!filterQuery) {
      setJobs([]);
      return;
    }

    const fetchJobs = async () => {
      const jobsData = await operationsSearch({
        data: {},
        filter: {
          oDataFilter: filterQuery,
          orderBy: ['ScheduledStartDate asc'],
        },
        select: null,
        resetHistory: true,
        ignoreHistory: true,
      });

      if (jobsData?.searchResult?.length) {
        setJobs(jobsData.searchResult);
      }
    };

    const id = setTimeout(fetchJobs, 100);
    return () => clearTimeout(id);
  }, [batch?.links, batch?.linkStatus, ncFileStatus, productionBatchId]);

  const firstScheduledJob = jobs?.[0] || null;
  const rawOee = machine.demoOee ?? oeeData?.[machineId]?.oee;
  const oeeValue = rawOee != null ? rawOee * 100 : null;

  const isActiveMachineStatus = status === machineStatuses.ACTIVE;

  const itemBackgroundColor =
    machineActive || isActiveMachineStatus ? 'Wallboard/Light/Productive Tile' : 'Wallboard/Light/Not Productive Tile';

  const isFirstTile = index === 0;

  const tourIds = isFirstTile
    ? {
        partImage: WALLBOARD_TOUR_IDS.PART_IMAGE,
        productivityTimeline: WALLBOARD_TOUR_IDS.PRODUCTIVITY_TIMELINE,
        batchTimeline: WALLBOARD_TOUR_IDS.BATCH_TIMELINE,
        piecesProgress: WALLBOARD_TOUR_IDS.PIECES_PROGRESS,
        oeeGauge: WALLBOARD_TOUR_IDS.OEE_GAUGE,
        scheduleGauge: WALLBOARD_TOUR_IDS.SCHEDULE_GAUGE,
      }
    : undefined;

  const tileWebProps = isWeb && isFirstTile ? { dataSet: { tourId: WALLBOARD_TOUR_IDS.MACHINE_TILE } } : {};
  const headerWebProps = isWeb && isFirstTile ? { dataSet: { tourId: WALLBOARD_TOUR_IDS.TILE_HEADER } } : {};

  return {
    url,
    batch,
    jobs,
    firstScheduledJob,
    oeeValue,
    machineActive,
    isActiveMachineStatus,
    itemBackgroundColor,
    tourIds,
    tileWebProps,
    headerWebProps,
    machineId,
    shortName,
    machineStatus,
    oldMachineStatusDate,
    oldMachineStatus,
    productionBatchId,
  };
};

export default useManageItem;
